import {useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {ArrowRight, Plus, ShieldAlert} from "lucide-react";
import {Link} from "react-router-dom";
import {useCapabilities} from "../../shared/auth/CapabilitiesContext";
import {AdminTable} from "../../shared/components/AdminTable";
import {DataState} from "../../shared/components/DataState";
import {StatusBadge} from "../../shared/components/StatusBadge";
import {formatExactMoney} from "../../shared/finance/decimal";
import {mapFinanceError} from "../../shared/finance/errors";
import {formatDateTime} from "../../shared/utils/format";
import {listSplitBills, splitBillKeys} from "./api";
import {getSplitGroupStatusPresentation} from "./statusMachine";
import {useSplitCountryScope} from "./useSplitCountryScope";
import {useTranslation} from "../../shared/i18n";
import "./splitBills.css";
import {PageSizeSelect} from "../../shared/components/PageSizeSelect";

const STATUS_OPTIONS = ["pending", "partially_paid", "paid", "cancelled", "expired"];

const progress = (parts) => {
    const paid = parts.filter(({status}) => status === "paid").length;
    const pending = parts.filter(({status}) => status === "pending").length;
    return {paid, pending, terminal: parts.length - paid - pending};
};

export const SplitBillsPage = () => {
    const {t} = useTranslation();
    const capabilities = useCapabilities();
    const {countryScope, scopeBlock} = useSplitCountryScope(capabilities);
    const canView = capabilities.hasAction("split_bills.view");
    const [status, setStatus] = useState("");
    const [limit, setLimit] = useState(50);
    const filters = useMemo(() => ({status, limit}), [limit, status]);
    const query = useQuery({
        queryKey: splitBillKeys.list(countryScope, filters),
        queryFn: () => listSplitBills({
            countryScope,
            actorCountryId: capabilities.country?.id,
            status,
            limit,
        }),
        enabled: canView && countryScope !== null && scopeBlock === null,
        retry: false,
    });

    if (!canView) {
        return <DataState title={t("money.splitBills.unavailableTitle")} copy={t("money.splitBills.unavailableCopy")} />;
    }

    const columns = [
        {
            key: "branch",
            label: t("money.splitBills.branch"),
            render: ({group}) => (
                <div>
                    <span className="admin-strong">{group.branch_name}</span>
                    <div className="admin-muted">{group.personal_name || t("money.splitBills.noStaff")}</div>
                </div>
            ),
        },
        {
            key: "amount",
            label: t("money.splitBills.amount"),
            render: ({group}) => (
                <div>
                    <span className="admin-strong">{formatExactMoney(group.original_amount)}</span>
                    <div className="admin-muted">{t("money.splitBills.partsCount", {count: group.parts_count})}</div>
                </div>
            ),
        },
        {
            key: "status",
            label: t("money.splitBills.status"),
            render: ({group}) => {
                const presentation = getSplitGroupStatusPresentation(group.status);
                return <StatusBadge tone={presentation.tone}>{t(`money.splitBills.statuses.${presentation.known ? group.status : "unknown"}`)}</StatusBadge>;
            },
        },
        {
            key: "progress",
            label: t("money.splitBills.progress"),
            render: ({group, parts}) => {
                const counts = progress(parts);
                return (
                    <div>
                        <span>{t("money.splitBills.paidOf", {paid: counts.paid, total: group.parts_count})}</span>
                        <div className="admin-muted">
                            {t("money.splitBills.progressDetail", {pending: counts.pending, terminal: counts.terminal})}
                        </div>
                    </div>
                );
            },
        },
        {
            key: "time",
            label: t("money.splitBills.deadline"),
            render: ({group}) => (
                <div>
                    <span>{formatDateTime(group.expires_at)}</span>
                    <div className="admin-muted">{t("money.splitBills.createdAt", {time: formatDateTime(group.created_at)})}</div>
                </div>
            ),
        },
        {
            key: "action",
            label: "",
            render: ({group}) => (
                <Link
                    className="split-row-link"
                    to={`/finance/split-bills/${group.id}`}
                    aria-label={t("money.splitBills.openAria", {id: group.id})}
                    title={t("money.splitBills.open")}
                >
                    <ArrowRight aria-hidden="true" size={17} />
                </Link>
            ),
        },
    ];

    return (
        <div className="admin-page split-page">
            <header className="admin-page-header split-page-header">
                <div>
                    <h1 className="admin-page-title">{t("money.splitBills.title")}</h1>
                    <p className="admin-page-copy">
                        {t("money.splitBills.copy")}
                    </p>
                </div>
                {capabilities.hasAction("split_bills.create") && scopeBlock === null ? (
                    <Link className="admin-button admin-button-primary" to="/finance/split-bills/new">
                        <Plus aria-hidden="true" size={16} /> {t("money.splitBills.create")}
                    </Link>
                ) : null}
            </header>

            {scopeBlock ? (
                <p className="split-scope-warning">
                    <ShieldAlert aria-hidden="true" size={16} /> {scopeBlock.title}
                </p>
            ) : null}

            <div className="admin-toolbar split-filter-bar">
                <label className="admin-field">
                    <span>{t("money.splitBills.status")}</span>
                    <select
                        aria-label={t("money.splitBills.statusAria")}
                        value={status}
                        disabled={Boolean(scopeBlock)}
                        onChange={(event) => setStatus(event.target.value)}
                    >
                        <option value="">{t("money.splitBills.all")}</option>
                        {STATUS_OPTIONS.map((value) => (
                            <option key={value} value={value}>
                                {t(`money.splitBills.statuses.${value}`)}
                            </option>
                        ))}
                    </select>
                </label>
                <PageSizeSelect value={limit} disabled={Boolean(scopeBlock)} onChange={setLimit} />
            </div>

            {scopeBlock ? (
                <DataState title={scopeBlock.title} copy={scopeBlock.copy} />
            ) : query.isError ? (
                <DataState
                    title={t("money.splitBills.loadError")}
                    copy={mapFinanceError(query.error).message}
                />
            ) : (
                <AdminTable
                    columns={columns}
                    rows={(query.data || []).map((snapshot) => ({
                        ...snapshot,
                        id: snapshot.group.id,
                    }))}
                    loading={query.isLoading}
                    emptyText={t("money.splitBills.empty")}
                />
            )}
        </div>
    );
};
